// Activity tracking functions
import {
  collection,
  doc,
  addDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore'
import { db } from './config'

const ACTIVITIES_COLLECTION = 'activities'

/**
 * Create a new activity
 * @param {Object} activityData - Activity data
 * @param {string} userId - Current user ID
 * @returns {Promise<string>} Document ID
 */
export const createActivity = async (activityData, userId) => {
  try {
    const docRef = await addDoc(collection(db, ACTIVITIES_COLLECTION), {
      ...activityData,
      owner: userId,
      createdAt: serverTimestamp()
    })
    return docRef.id
  } catch (error) {
    console.error('Error creating activity:', error)
    throw error
  }
}

/**
 * Helper: Log an email activity
 * @param {string} contactId - Contact ID
 * @param {string} userId - User ID
 * @param {Object} emailData - Email details
 */
export const logEmail = async (contactId, userId, emailData = {}) => {
  return createActivity({
    type: 'email',
    contactId,
    ...emailData
  }, userId)
}

/**
 * Helper: Log a call activity
 * @param {string} contactId - Contact ID
 * @param {string} userId - User ID
 * @param {Object} callData - Call details
 */
export const logCall = async (contactId, userId, callData = {}) => {
  return createActivity({
    type: 'call',
    contactId,
    ...callData
  }, userId)
}

/**
 * Helper: Log a meeting activity
 * @param {string} contactId - Contact ID
 * @param {string} userId - User ID
 * @param {Object} meetingData - Meeting details
 */
export const logMeeting = async (contactId, userId, meetingData = {}) => {
  return createActivity({
    type: 'meeting',
    contactId,
    ...meetingData
  }, userId)
}

/**
 * Helper: Log a website visit activity
 * @param {string} contactId - Contact ID
 * @param {string} userId - User ID
 * @param {Object} visitData - Visit details
 */
export const logWebsiteVisit = async (contactId, userId, visitData = {}) => {
  return createActivity({
    type: 'website_visit',
    contactId,
    ...visitData
  }, userId)
}

/**
 * Helper: Log a LinkedIn message activity
 * @param {string} contactId - Contact ID
 * @param {string} userId - User ID
 * @param {Object} messageData - Message details
 */
export const logLinkedInMessage = async (contactId, userId, messageData = {}) => {
  return createActivity({
    type: 'linkedin_message',
    contactId,
    ...messageData
  }, userId)
}

/**
 * Get activities for a contact
 * @param {string} contactId - Contact ID
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of activities
 */
export const getContactActivities = async (contactId, userId) => {
  try {
    const q = query(
      collection(db, ACTIVITIES_COLLECTION),
      where('contactId', '==', contactId),
      where('owner', '==', userId),
      orderBy('createdAt', 'desc')
    )
    
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting activities:', error)
    throw error
  }
}

/**
 * Get all activities for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of activities
 */
export const getUserActivities = async (userId) => {
  try {
    const q = query(
      collection(db, ACTIVITIES_COLLECTION),
      where('owner', '==', userId),
      orderBy('createdAt', 'desc')
    )
    
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting user activities:', error)
    throw error
  }
}

/**
 * Subscribe to contact activities (real-time)
 * @param {string} contactId - Contact ID
 * @param {string} userId - User ID
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const subscribeToContactActivities = (contactId, userId, callback) => {
  const q = query(
    collection(db, ACTIVITIES_COLLECTION),
    where('contactId', '==', contactId),
    where('owner', '==', userId),
    orderBy('createdAt', 'desc')
  )
  
  return onSnapshot(q, (snapshot) => {
    const activities = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
    callback(activities)
  }, (error) => {
    console.error('Error subscribing to activities:', error)
  })
}

/**
 * Delete all activities for a contact
 * @param {string} contactId - Contact ID
 * @param {string} userId - User ID (optional, for security)
 * @returns {Promise<void>}
 */
export const deleteContactActivities = async (contactId, userId = null) => {
  try {
    // Build query - include userId filter if provided for security
    const queryConstraints = [
      where('contactId', '==', contactId)
    ]
    
    if (userId) {
      queryConstraints.push(where('owner', '==', userId))
    }
    
    const q = query(
      collection(db, ACTIVITIES_COLLECTION),
      ...queryConstraints
    )
    
    const querySnapshot = await getDocs(q)
    
    // Delete all activities for this contact
    if (querySnapshot.docs.length > 0) {
      const deletePromises = querySnapshot.docs.map(docSnapshot => 
        deleteDoc(doc(db, ACTIVITIES_COLLECTION, docSnapshot.id))
      )
      await Promise.all(deletePromises)
      console.log(`Deleted ${querySnapshot.docs.length} activities for contact ${contactId}`)
    } else {
      console.log(`No activities found for contact ${contactId}`)
    }
  } catch (error) {
    console.error('Error deleting contact activities:', error)
    // Don't throw - allow contact deletion to proceed even if activity deletion fails
    // This prevents a single activity deletion error from blocking contact deletion
    console.warn('Continuing with contact deletion despite activity deletion error')
  }
}

